import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * ConeWorks : offres de la fiche produit.
 *
 * - Offre « solo » : une unité du produit consulté (variante sélectionnée).
 * - Offre « pack » : une unité du produit consulté + une unité des filtres (deux lignes distinctes).
 *
 * Tout vient de Shopify : produit, variantes, prix, prix de comparaison, stock. La remise du pack est
 * une vraie remise automatique Shopify, appliquée au panier et au paiement : ce script n'invente
 * aucun prix, il affiche seulement le total attendu lorsque le réglage « remise active » est coché.
 */

const POLL_INTERVAL_MS = 300;

class CwOffers extends HTMLElement {
  /** @type {any} */
  #config = {};
  /** @type {Map<number, any>} */
  #variants = new Map();
  /** @type {number | null} */
  #variantId = null;
  /** @type {'solo' | 'pack' | null} */
  #offer = null;
  #busy = false;
  /** @type {Map<string, Intl.NumberFormat>} */
  #formatters = new Map();
  /** @type {number | undefined} */
  #pollTimer;
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    try {
      this.#config = JSON.parse(this.querySelector('script[data-cwo-config]')?.textContent || '{}');
    } catch (error) {
      console.warn('[cw-offers] Configuration illisible', error);
      return;
    }

    for (const variant of this.#config.product?.variants || []) this.#variants.set(variant.id, variant);
    this.#variantId = this.#readVariantId() ?? this.#config.selectedVariantId;
    this.#form = this.closest('form');

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.#form?.addEventListener('submit', this.#onFormSubmit);
    document.addEventListener('click', this.#scheduleVariantSync, true);
    document.addEventListener('change', this.#scheduleVariantSync, true);
    document.addEventListener(StandardEvents.productSelect, this.#scheduleVariantSync);
    window.addEventListener('pageshow', this.#scheduleVariantSync);
    this.#pollTimer = window.setInterval(this.#syncVariant, POLL_INTERVAL_MS);

    this.#render({ initial: true });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.#form?.removeEventListener('submit', this.#onFormSubmit);
    document.removeEventListener('click', this.#scheduleVariantSync, true);
    document.removeEventListener('change', this.#scheduleVariantSync, true);
    document.removeEventListener(StandardEvents.productSelect, this.#scheduleVariantSync);
    window.removeEventListener('pageshow', this.#scheduleVariantSync);
    window.clearInterval(this.#pollTimer);
  }

  /** @param {string} name @returns {HTMLElement | null} */
  #el(name) {
    return this.querySelector(`[data-cwo="${name}"]`);
  }

  /** @param {string} path */
  #url(path) {
    return `${String(this.#config.root || '/').replace(/\/$/, '')}/${path}`;
  }

  /** @param {number} cents @returns {string} */
  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'fr-FR';
    const key = `${locale}|${currency}`;
    let formatter = this.#formatters.get(key);

    if (!formatter) {
      try {
        formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
      } catch (_) {
        formatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
      }
      this.#formatters.set(key, formatter);
    }

    return formatter.format(cents / 100);
  }

  /* ------------------------------------------------------------------ */
  /* Variante du produit consulté                                         */
  /* ------------------------------------------------------------------ */

  /** Source de vérité : le champ « id » du formulaire produit, sinon le paramètre ?variant= de l'URL. */
  #readVariantId() {
    const form = this.closest('form');
    const field = form?.querySelector('input[name="id"]') || form?.querySelector('select[name="id"]');
    const fromField = Number(/** @type {HTMLInputElement | null} */ (field)?.value);
    if (fromField && this.#variants.has(fromField)) return fromField;

    const fromUrl = Number(new URL(location.href).searchParams.get('variant'));
    if (fromUrl && this.#variants.has(fromUrl)) return fromUrl;

    return null;
  }

  #syncVariant = () => {
    const id = this.#readVariantId();
    if (id && id !== this.#variantId) {
      this.#variantId = id;
      this.#render();
    }
  };

  /** Laisse les autres scripts (sélecteur de variante) terminer avant de relire la variante. */
  #scheduleVariantSync = () => {
    window.setTimeout(this.#syncVariant, 0);
    window.setTimeout(this.#syncVariant, 120);
  };

  get #variant() {
    return this.#variants.get(/** @type {number} */ (this.#variantId)) || this.#variants.values().next().value;
  }

  /* ------------------------------------------------------------------ */
  /* Offres                                                               */
  /* ------------------------------------------------------------------ */

  get #soloAvailable() {
    return Boolean(this.#variant?.available);
  }

  get #packAvailable() {
    return Boolean(this.#config.offer2 && this.#config.filters && this.#config.filters.variant?.available && this.#soloAvailable);
  }

  /** Total attendu du pack : somme des prix actuels moins la vraie remise automatique Shopify. */
  #packPrices() {
    const variant = this.#variant;
    const filters = this.#config.filters?.variant;
    if (!variant || !filters) return null;

    const total = variant.price + filters.price;

    // La remise affichée est celle réellement configurée dans Shopify pour ce produit (métafield
    // coneworks.pack_discount, écrit en même temps que la remise automatique). Elle disparaît si les prix
    // ont changé depuis : on n'affiche jamais une réduction que le paiement n'appliquerait pas.
    const known = this.#config.packDiscount;
    const matches =
      this.#config.discountReady &&
      known &&
      known.main === variant.price &&
      known.filters === filters.price &&
      known.filtersVariantId === filters.id;
    const discount = matches ? Number(known.amount) : 0;

    return { total, discount, final: total - discount };
  }

  /** @param {{ initial?: boolean }} [options] */
  #render({ initial = false } = {}) {
    const variant = this.#variant;
    if (!variant) return;

    const soloCard = this.#el('card-solo');
    const packCard = this.#el('card-pack');
    const soloInput = /** @type {HTMLInputElement | null} */ (this.#el('input-solo'));
    const packInput = /** @type {HTMLInputElement | null} */ (this.#el('input-pack'));
    const packAvailable = this.#packAvailable;

    // Offre solo : photo, prix et prix de comparaison réels de la variante.
    const soloImage = /** @type {HTMLImageElement | null} */ (this.#el('solo-image'));
    const image = variant.image || this.#config.product.image;
    if (soloImage && image && soloImage.getAttribute('src') !== image) {
      soloImage.src = image;
      soloImage.alt = '';
    }
    const soloPrice = this.#el('solo-price');
    if (soloPrice) soloPrice.textContent = this.#money(variant.price);
    const soloCompare = this.#el('solo-compare');
    if (soloCompare) {
      const onSale = variant.compareAt > variant.price;
      soloCompare.hidden = !onSale;
      if (onSale) soloCompare.textContent = this.#money(variant.compareAt);
    }

    // Offre pack : produit consulté + filtres.
    if (packCard) {
      const filters = this.#config.filters;
      const mainImage = /** @type {HTMLImageElement | null} */ (this.#el('pack-main-image'));
      if (mainImage && image && mainImage.getAttribute('src') !== image) mainImage.src = image;
      const filtersImage = /** @type {HTMLImageElement | null} */ (this.#el('pack-filters-image'));
      if (filtersImage && filters?.image && filtersImage.getAttribute('src') !== filters.image) filtersImage.src = filters.image;

      const mainName = this.#el('pack-main-name');
      if (mainName) {
        const suffix = variant.title && variant.title !== 'Default Title' ? ` · ${variant.title}` : '';
        mainName.textContent = `${this.#config.product.title}${suffix}`;
      }
      const filtersName = this.#el('pack-filters-name');
      if (filtersName && filters) {
        const suffix = filters.variant.title && filters.variant.title !== 'Default Title' ? ` · ${filters.variant.title}` : '';
        filtersName.textContent = `${filters.productTitle}${suffix}`;
      }

      const prices = this.#packPrices();
      const priceEl = this.#el('pack-price');
      const compareEl = this.#el('pack-compare');
      const offEl = this.#el('pack-off');
      if (prices && priceEl) {
        priceEl.textContent = this.#money(prices.final);
        const discounted = prices.discount > 0;
        if (compareEl) {
          compareEl.hidden = !discounted;
          if (discounted) compareEl.textContent = this.#money(prices.total);
        }
        if (offEl) {
          offEl.hidden = !discounted;
          if (discounted) offEl.textContent = String(this.#config.discountLabel).replace('{percent}', String(this.#config.discountPercent));
        }
      }

      this.#el('pack-unavailable')?.toggleAttribute('hidden', packAvailable);
      packCard.classList.toggle('is-disabled', !packAvailable);
      if (packInput) packInput.disabled = !packAvailable;
    }

    // Offre sélectionnée : le pack par défaut s'il est disponible, sinon le produit seul.
    const wanted = this.#offer;
    if (wanted === 'pack' && !packAvailable) this.#offer = 'solo';
    else if (initial || !this.#offer) this.#offer = packAvailable && packCard ? 'pack' : 'solo';

    if (this.#offer === 'solo' && !soloCard) this.#offer = packAvailable && packCard ? 'pack' : 'solo';

    soloCard?.classList.toggle('is-selected', this.#offer === 'solo');
    packCard?.classList.toggle('is-selected', this.#offer === 'pack');
    if (soloInput) soloInput.checked = this.#offer === 'solo';
    if (packInput) packInput.checked = this.#offer === 'pack';

    // Bouton d'achat.
    const soldOut = !this.#soloAvailable;
    for (const button of /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('.cwo__button'))) {
      button.disabled = soldOut || this.#busy;
    }
    const label = this.#el('add-label');
    if (label) label.textContent = soldOut ? this.#config.labels.soldOut : this.#busy ? this.#config.labels.adding : this.#config.labels.add;
    this.classList.toggle('is-busy', this.#busy);
  }

  #onChange = (/** @type {Event} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || input.disabled) return;

    this.#offer = input.value === 'pack' ? 'pack' : 'solo';
    this.#hideError();
    this.#render();
  };

  #onClick = (/** @type {MouseEvent} */ event) => {
    // Correctif : un clic sur le texte du bouton (sous-élément data-cwo="add-label") doit aussi déclencher l'ajout.
    const target = event.target instanceof Element ? event.target.closest('[data-cwo="add"], [data-cwo="buy-now"]') : null;
    const action = target?.getAttribute('data-cwo');

    if (action === 'add') {
      event.preventDefault();
      this.#add();
    } else if (action === 'buy-now') {
      event.preventDefault();
      this.#add({ buyNow: true });
    }
  };

  /** Entrée clavier dans le formulaire : on passe par le même chemin que le bouton. */
  #onFormSubmit = (/** @type {SubmitEvent} */ event) => {
    event.preventDefault();
    this.#add();
  };

  /* ------------------------------------------------------------------ */
  /* Ajout au panier                                                      */
  /* ------------------------------------------------------------------ */

  /** @returns {{ id: number, quantity: number }[] | null} */
  #lines() {
    const variant = this.#variant;
    if (!variant || !this.#soloAvailable) return null;

    const lines = [{ id: variant.id, quantity: 1 }];
    if (this.#offer === 'pack') {
      if (!this.#packAvailable) return null;
      lines.push({ id: this.#config.filters.variant.id, quantity: 1 });
    }
    return lines;
  }

  /** @param {{ buyNow?: boolean }} [options] */
  async #add({ buyNow = false } = {}) {
    if (this.#busy) return;

    const lines = this.#lines();
    if (!lines) return;

    this.#busy = true;
    this.#hideError();
    this.#render();

    try {
      const response = await fetch(this.#url('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: lines }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || this.#config.labels.error);
      }

      if (buyNow) {
        window.location.href = this.#url('checkout');
        return;
      }

      const cart = await (
        await fetch(this.#url('cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      ).json();

      // Le panier latéral s'ouvre via l'événement standard du thème, après un ajout réussi.
      const deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'cw-offers', didError: false },
      });

      const status = this.#el('status');
      if (status) status.textContent = `${this.#config.labels.add} : ${cart.item_count}`;
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : this.#config.labels.error);
    } finally {
      if (!buyNow) {
        this.#busy = false;
        this.#render();
      }
    }
  }

  /** @param {string} message */
  #showError(message) {
    const element = this.#el('error');
    if (!element) return;
    element.textContent = message;
    element.hidden = false;
  }

  #hideError() {
    const element = this.#el('error');
    if (element) {
      element.hidden = true;
      element.textContent = '';
    }
  }
}

if (!customElements.get('cw-offers')) {
  customElements.define('cw-offers', CwOffers);
}
